import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { formatPrice } from "@/lib/format-utils";
import { TagIcon } from "@/lib/tag-utils";
import { Link } from "react-router-dom";
import { BUSINESSES_PATH, PERSONS_PATH } from "@/lib/navigation";
import { ClientBalanceEntry } from "@turizi/shared";
import {
    IconTag,
    IconBuilding,
    IconArrowDownLeft,
    IconArrowUpRight,
    IconScale,
    IconShoppingCartDollar
} from "@tabler/icons-react";

interface BalanceTableProps {
    clients: ClientBalanceEntry[];
    myBusinessName: string;
    isPerson?: boolean;
}

export default function BalanceTable({ clients, isPerson = false, myBusinessName }: BalanceTableProps) {
    const { t } = useTranslation();

    const sortedClients = useMemo(() => {
        if (!clients) return [];
        return [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    }, [clients]);

    const linkToBalanace = (client: ClientBalanceEntry) => {
        if (client.category === 'person') {
            return `${PERSONS_PATH}/${client.id}`;
        }
        return `${BUSINESSES_PATH}/${client.id}`;
    }

    return (
        <div className="subtle-table-sm">
            <table>
                <thead>
                    <tr>
                        <th>
                            <div className="flex items-center gap-1.5 px-4">
                                <span>{t('client')}</span>
                            </div>
                        </th>
                        <th className="number">
                            <div className="flex flex-col items-end gap-1 justify-end">
                                <div className="flex items-center gap-1">
                                    <IconShoppingCartDollar size={13} className="shrink-0" />
                                    <span>{t('sold')}</span>
                                </div>
                                {/* <div className="font-bold">
                                    DE {myBusinessName}
                                </div> */}
                            </div>
                        </th>
                        {!isPerson && (
                            <>
                                <th className="number">
                                    <div className="flex flex-col items-end gap-1 justify-end">
                                        <div className="flex items-center gap-1">
                                            <IconTag size={13} className="shrink-0" />
                                            <span>{t('purchases')}</span>
                                        </div>
                                        {/* <div className="font-bold">
                                            A {myBusinessName}
                                        </div> */}
                                    </div>
                                </th>
                                <th className="number">
                                    <div className="flex flex-col items-end gap-1 justify-end">
                                        <div className="flex items-center gap-1">
                                            <IconBuilding size={13} className="shrink-0" />
                                            <span>{t('providers')}</span>
                                        </div>
                                        {/* <div className="font-bold">
                                            A {myBusinessName}
                                        </div> */}
                                    </div>
                                </th>
                            </>
                        )}
                        <th className="number">
                            <div className="flex flex-col items-end gap-1 justify-end">
                                <div className="flex items-center gap-1">
                                    <IconArrowDownLeft size={13} className="shrink-0" />
                                    <span>{t('received')} $</span>
                                </div>
                            </div>
                        </th>
                        <th className="number">
                            <div className="flex flex-col items-end gap-1 justify-end">
                                <div className="flex items-center gap-1">
                                    <IconArrowUpRight size={13} className="shrink-0" />
                                    <span>{t('sent')} $</span>
                                </div>
                            </div>
                        </th>
                        <th className="number">
                            <div className="flex items-center gap-1 justify-end">
                                <IconScale size={13} className="shrink-0" />
                                <span>{t('balance')}</span>
                            </div>
                        </th>
                        <th className="number">
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedClients.map((client) => {
                        const balances = Object.entries(client.balances || {}).map(([currency, bal]) => ({
                            currency,
                            consumed: bal.consumed || 0,
                            sold: bal.sold || 0,
                            provided: bal.provided || 0,
                            paid: bal.paid || 0,
                            payout: bal.payout || 0,
                            total: bal.total || 0
                        }));

                        return (
                            <tr
                                key={client.key}
                                className="clickable-row"
                            >
                                <td>
                                    <Link
                                        className="main-link"
                                        to={linkToBalanace(client)}
                                    >
                                        <div className="flex items-center">
                                            <TagIcon
                                                name={client?.category}
                                                size="xs"
                                                hideText
                                                noBackground
                                            />
                                            <div className="ml-4">
                                                <div className="text-sm text-gray-900">{client.name || '??'}</div>
                                                <div className="text-xs text-gray-800">{t(client?.category || '')} {client.city} {client.country_code}</div>
                                            </div>
                                        </div>
                                    </Link>
                                </td>
                                {/* Consumed */}
                                <td>
                                    <div className="flex flex-col gap-1 items-end">
                                        {balances.filter(b => (isPerson ? b.consumed : b.sold) > 0).map(b => (
                                            <div key={b.currency}>
                                                <span>{formatPrice(isPerson ? b.consumed : b.sold)}</span> <span className="text-gray-400 font-medium">{b.currency}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                {!isPerson && (
                                    <>
                                        {/* Sold */}
                                        <td>
                                            <div className="flex flex-col gap-1 items-end">
                                                {balances.filter(b => b.consumed > 0).map(b => (
                                                    <div key={b.currency}>
                                                        <span>{formatPrice(b.consumed)}</span> <span className="text-gray-400 font-medium">{b.currency}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        {/* Provider */}
                                        <td>
                                            <div className="flex flex-col gap-1 items-end">
                                                {balances.filter(b => b.provided > 0).map(b => (
                                                    <div key={b.currency}>
                                                        <span>{formatPrice(b.provided)}</span> <span className="text-gray-400 font-medium">{b.currency}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </>
                                )}
                                {/* Received */}
                                <td>
                                    <div className="flex flex-col gap-1 items-end">
                                        {balances.filter(b => Math.abs(b.paid) > 0).map(b => (
                                            <div key={b.currency}>
                                                <span>{formatPrice(b.paid)}</span> <span className="text-gray-400 font-medium">{b.currency}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                {/* Sent */}
                                <td>
                                    <div className="flex flex-col gap-1 items-end">
                                        {balances.filter(b => Math.abs(b.payout) > 0).map(b => (
                                            <div key={b.currency}>
                                                <span>{formatPrice(b.payout)}</span> <span className="text-gray-400 font-medium">{b.currency}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                {/* Balance */}

                                <td>
                                    <div className="flex flex-col gap-1.5 items-end">
                                        {balances.map(b => {
                                            const isWeOwe = b.total > 0;
                                            const isTheyOwe = b.total < 0;
                                            return (
                                                <div key={b.currency}>
                                                    <div
                                                        className={`
                                                            px-2.5 py-1 rounded-lg w-fit
                                                            ${isWeOwe && 'bg-green-100'}
                                                            ${isTheyOwe && 'bg-red-100'}
                                                        `}
                                                    >
                                                        <div className="text-xs font-bold">
                                                            <span className="font-light">$</span>{formatPrice(Math.abs(b.total))} <span className="text-xs font-medium text-gray-500">{b.currency}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </td>
                                <td>
                                    <div className="flex flex-col gap-1.5 items-start">
                                        {balances.map(b => {
                                            const isWeOwe = b.total > 0;
                                            const isTheyOwe = b.total < 0;
                                            return (
                                                <div key={b.currency} className={`
                                                px-2.5 py-1 rounded-lg w-fit text-xxs font-medium
                                                ${isWeOwe && 'bg-green-100'}
                                                ${isTheyOwe && 'bg-red-100'}
                                                `}>
                                                    {isWeOwe && t('client_surplus_money')}
                                                    {isTheyOwe && t('owes')}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
